/**
 * "Paid in full, UMED pending" (Isaiah Cotto's ruling, 2026-10-15; Josie Evans on Ruby Plummer: she paid in full, and
 * the UMED is still needed for the reimbursement).
 *
 * A fact on the UMED case and nothing else: the case keeps its stage, its word and every list it is in, and goes on
 * through submission and approval. It is NOT the PAID IN FULL disposition, which ends the UMED (stage Handed to Finance,
 * finance_state PAID_IN_FULL -- with no Finance handoff opened). Marked and cleared with a reason, by the approval reviewer (UMED board) or Finance (its dossier); read by all.
 * Once approved, Finance's handoff says "Resident paid in full: reimbursement owed".
 *
 * The form takes the act as a function, so the UMED board and the Finance dossier post to their own routes with the
 * same words. Every state is said in words, never by colour alone.
 */
import React, { useEffect, useRef, useState } from 'react';
import { freshKey, shortDate, RESUBMITTAL_OPEN_WORDS } from './umedApi';
import type { PaidInFullMark, ApiResult } from './umedApi';

export const PAID_IN_FULL_LABEL = 'Paid in full · UMED pending';
export const PAID_IN_FULL_MARK_EFFECT = 'Records that the resident has paid in full while the UMED goes on. Nothing else changes: the case keeps its status and stays in its lists until DCF approves it, and Finance then sees "Resident paid in full: reimbursement owed".';
export const PAID_IN_FULL_CLEAR_EFFECT = 'Clears the paid-in-full mark (a mistake, or the payment did not stand). The history keeps both.';

const shown = (m: PaidInFullMark) => [
  m.paid_on ? `paid ${shortDate(m.paid_on)}` : null,
  m.recorded_by_name ? `recorded by ${m.recorded_by_name}` : null,
  m.via === 'FINANCE' ? 'from Finance' : null,
].filter(Boolean).join(' · ');

/** The chip, where the mark is in force. */
export function PaidInFullChip({ mark }: { mark: PaidInFullMark | null | undefined }) {
  if (!mark || mark.state !== 'MARKED') return null;
  const more = shown(mark);
  return (
    <span className="um-tag um-pif" data-testid="paid-in-full-chip" title={`${PAID_IN_FULL_LABEL}${mark.reason ? `: ${mark.reason}` : ''}${more ? ` (${more})` : ''}`}>
      {PAID_IN_FULL_LABEL}
      <span className="um-sr-only">{mark.reason ? `: ${mark.reason}` : ''}{more ? `. ${more}` : ''}</span>
    </span>
  );
}

type Outcome = ApiResult<{ warnings?: string[] } | unknown>;
const said = (r: Outcome): string => {
  if (r.status === undefined && !r.code) return 'Could not reach the server. Nothing was recorded.';
  if (r.status !== undefined && r.status >= 500) return 'The server failed while recording this. Nothing was recorded.';
  return r.message || r.error || (r.code ? `Refused (${r.code}).` : 'The server refused this.');
};

/**
 * Mark or clear, with the reason (and, to mark, the day it was paid). `act` posts to the caller's own route. `effect`:
 * the sentence said before anything is saved, when the caller has its own (the UMED board's "Paid in full" group,
 * 2026-10-20); otherwise the mark's or the clearing's default sentence.
 */
export function PaidInFullForm({ mode, name, act, onClose, onDone, effect }: {
  mode: 'mark' | 'clear';
  name: string;
  act: (body: { reason: string; paid_on?: string | null; idempotency_key: string }) => Promise<Outcome>;
  onClose: () => void;
  onDone: (msg: string) => void;
  effect?: React.ReactNode;
}) {
  const [reason, setReason] = useState('');
  const [paidOn, setPaidOn] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef(freshKey());
  const first = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => { first.current?.focus({ preventScroll: true }); }, []);
  const label = mode === 'mark' ? `Mark ${name} paid in full, UMED pending` : `Clear paid in full for ${name}`;
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await act({ reason: reason.trim(), ...(mode === 'mark' ? { paid_on: paidOn || null } : {}), idempotency_key: key.current });
      if (!r.success) { setErr(said(r)); return; }
      key.current = freshKey();
      const warnings = ((r.data || {}) as { warnings?: string[] }).warnings || [];
      onClose();
      onDone(mode === 'mark'
        ? `${name} is marked ${PAID_IN_FULL_LABEL}. The UMED goes on.${warnings.includes('RESUBMITTAL_OPEN') ? ` ${RESUBMITTAL_OPEN_WORDS}` : ''}`
        : `The paid-in-full mark for ${name} is cleared.`);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <div className="um-form um-rowpanel" role="group" aria-label={label} onClick={(e) => e.stopPropagation()}
      onKeyDown={busy ? undefined : (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } }}>
      <p className="um-rowpanel-what" data-testid="pif-effect">{effect ?? (mode === 'mark' ? PAID_IN_FULL_MARK_EFFECT : PAID_IN_FULL_CLEAR_EFFECT)}</p>
      <label className="um-rowfield"><span>{mode === 'mark' ? 'Why (required)' : 'Why it is cleared (required)'}</span>
        <textarea ref={first} rows={2} maxLength={500} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)}
          aria-label={mode === 'mark' ? 'Why the resident is marked paid in full' : 'Why the paid-in-full mark is cleared'} /></label>
      {mode === 'mark' && (
        <label className="um-rowfield"><span>Day it was paid (optional)</span>
          <input type="date" value={paidOn} disabled={busy} onChange={(e) => setPaidOn(e.target.value)} aria-label="Day it was paid (optional)" /></label>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="btns um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || reason.trim().length < 3} onClick={run}>
          {busy ? 'Recording…' : mode === 'mark' ? 'Record paid in full' : 'Clear the mark'}
        </button>
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
